import {
  constructRow,
  tableMemo,
  type Row,
  type RowData,
  type RowModel,
  type Table,
} from '@tanstack/react-table';

import type { TDataTableFeatures } from './types/features.types.js';
import type { TableMemoTable } from './types/table.types.js';

export function createCoreRowModel<TData extends RowData>(): (
  table: Table<TDataTableFeatures, TData>,
) => () => RowModel<TDataTableFeatures, TData> {
  return (table) => {
    return tableMemo({
      feature: 'coreRowModelsFeature',
      table: table as unknown as TableMemoTable,
      fnName: 'table.getCoreRowModel',
      memoDeps: () => [table.options.data, table.options.columns],
      fn: () => _createCoreRowModel(table, table.options.data),
    });
  };
}

function _createCoreRowModel<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
  data: ReadonlyArray<TData>,
): {
  rows: Array<Row<TDataTableFeatures, TData>>;
  flatRows: Array<Row<TDataTableFeatures, TData>>;
  rowsById: Record<string, Row<TDataTableFeatures, TData>>;
} {
  const rowModel: RowModel<TDataTableFeatures, TData> = {
    rows: [],
    flatRows: [],
    rowsById: {},
  };

  const accessRows = (
    originalRows: ReadonlyArray<TData>,
    depth = 0,
    parentRow?: Row<TDataTableFeatures, TData>,
  ): Array<Row<TDataTableFeatures, TData>> => {
    const rows = [] as Array<Row<TDataTableFeatures, TData>>;

    for (let i = 0; i < originalRows.length; i++) {
      // oxlint-disable-next-line typescript/no-non-null-assertion
      const originalRow = originalRows[i]!;
      // Make the row
      const row = constructRow(
        table,
        table.getRowId(originalRow, i, parentRow),
        originalRow,
        i,
        depth,
        undefined,
        parentRow?.id,
      );

      // Keep track of every row in a flat array
      rowModel.flatRows.push(row);
      // Also keep track of every row by its ID
      rowModel.rowsById[row.id] = row;
      // Push table row into parent
      rows.push(row);

      // Get the original subrows
      if (table.options.getSubRows) {
        row.originalSubRows = table.options.getSubRows(originalRow, i);

        // Then recursively access them
        if (row.originalSubRows?.length) {
          row.subRows = accessRows(row.originalSubRows, depth + 1, row);
        }
      }
    }

    return rows;
  };

  rowModel.rows = accessRows(data);

  return rowModel;
}
